/**
 * Environment configuration for MEQ Insights Platform
 * Handles environment-specific settings and URLs
 */

export interface EnvironmentConfig {
  environment: string;
  apiBaseUrl: string;
  frontendUrl: string;
  domain: string;
  secure: boolean;
  services: {
    landing: string;
    client1: string;
    client2: string;
    client3: string;
    jbsusa: string;
    auth: string;
    api: string;
  };
}

class ConfigService {
  private config: EnvironmentConfig;

  constructor() {
    this.config = this.loadConfiguration();
  }

  private loadConfiguration(): EnvironmentConfig {
    // Get configuration from environment variables (Vite)
    const environment = import.meta.env.VITE_ENVIRONMENT || this.detectEnvironment();
    const domain = import.meta.env.VITE_DOMAIN || this.detectDomain();
    const secure = this.parseBoolean(import.meta.env.VITE_SECURE) ?? this.detectSecure();
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || this.buildApiBaseUrl(domain, secure);

    return {
      environment,
      apiBaseUrl,
      frontendUrl: apiBaseUrl,
      domain,
      secure,
      services: this.buildServiceUrls(apiBaseUrl, environment, secure, domain)
    };
  }

  private buildServiceUrls(apiBaseUrl: string, environment: string, secure: boolean, domain: string) {
    const protocol = secure ? 'https' : 'http';
    
    // For local development, use specific ports if provided
    if (environment === 'local') {
      return {
        landing: apiBaseUrl,
        client1: import.meta.env.VITE_CLIENT1_PORT 
          ? `${protocol}://${domain.split(':')[0]}:${import.meta.env.VITE_CLIENT1_PORT}`
          : `${apiBaseUrl}/client1`,
        client2: import.meta.env.VITE_CLIENT2_PORT 
          ? `${protocol}://${domain.split(':')[0]}:${import.meta.env.VITE_CLIENT2_PORT}`
          : `${apiBaseUrl}/client2`,
        client3: import.meta.env.VITE_CLIENT3_PORT 
          ? `${protocol}://${domain.split(':')[0]}:${import.meta.env.VITE_CLIENT3_PORT}`
          : `${apiBaseUrl}/client3`,
        jbsusa: import.meta.env.VITE_JBSUSA_PORT 
          ? `${protocol}://${domain.split(':')[0]}:${import.meta.env.VITE_JBSUSA_PORT}`
          : `${apiBaseUrl}/jbsusa`,
        auth: import.meta.env.VITE_AUTH_PORT 
          ? `${protocol}://${domain.split(':')[0]}:${import.meta.env.VITE_AUTH_PORT}`
          : `${apiBaseUrl}/auth`,
        api: import.meta.env.VITE_API_PORT 
          ? `${protocol}://${domain.split(':')[0]}:${import.meta.env.VITE_API_PORT}`
          : `${apiBaseUrl}/api`
      };
    }

    // For dev/prod environments, use nginx proxy routing
    return {
      landing: apiBaseUrl,
      client1: `${apiBaseUrl}/client1`,
      client2: `${apiBaseUrl}/client2`,
      client3: `${apiBaseUrl}/client3`,
      jbsusa: `${apiBaseUrl}/jbsusa`,
      auth: `${apiBaseUrl}/auth`,
      api: `${apiBaseUrl}/api`
    };
  }

  private detectEnvironment(): string {
    // Detect environment from various sources
    if (import.meta.env.DEV) {
      return 'local';
    }
    
    if (import.meta.env.PROD) {
      const hostname = window.location.hostname;
      if (hostname.includes('dev') || hostname.includes('staging')) {
        return 'dev';
      }
      return 'prod';
    }

    return 'local';
  }

  private detectDomain(): string {
    // Use current hostname if no domain specified
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    if (port && !['80', '443'].includes(port)) {
      return `${hostname}:${port}`;
    }
    
    return hostname;
  }

  private detectSecure(): boolean {
    // Auto-detect HTTPS based on current protocol and environment
    if (window.location.protocol === 'https:') {
      return true;
    }
    
    // Development environments typically use HTTP
    const hostname = window.location.hostname;
    return !['localhost', '127.0.0.1'].includes(hostname);
  }

  private buildApiBaseUrl(domain: string, secure: boolean): string {
    const protocol = secure ? 'https' : 'http';
    return `${protocol}://${domain}`;
  }

  private parseBoolean(value: string | undefined): boolean | null {
    if (!value) return null;
    return ['true', '1', 'yes'].includes(value.toLowerCase());
  }

  /**
   * Get the current configuration
   */
  getConfig(): EnvironmentConfig {
    return this.config;
  }

  /**
   * Get URL for a specific service
   */
  getServiceUrl(service: keyof EnvironmentConfig['services'], path: string = ''): string {
    const baseUrl = this.config.services[service];
    if (!baseUrl) {
      throw new Error(`Unknown service: ${service}`);
    }
    
    if (path && !path.startsWith('/')) {
      path = `/${path}`;
    }
    
    return `${baseUrl}${path}`;
  }

  /**
   * Get deployment information for backwards compatibility
   */
  getDeploymentInfo() {
    return {
      environment: this.config.environment,
      nginx_proxy: this.config.apiBaseUrl,
      domain: this.config.domain,
      client1_route: '/client1',
      client2_route: '/client2',
      client3_route: '/client3',
      secure: this.config.secure,
      note: `All paths are routed through nginx proxy with JWT authentication in ${this.config.environment} environment`
    };
  }

  /**
   * Check if we're in development mode
   */
  isDevelopment(): boolean {
    return this.config.environment === 'local' || import.meta.env.DEV;
  }

  /**
   * Check if we're in production mode
   */
  isProduction(): boolean {
    return this.config.environment === 'prod' && import.meta.env.PROD;
  }

  /**
   * Get environment-specific debugging info
   */
  getDebugInfo() {
    return {
      config: this.config,
      viteEnv: {
        DEV: import.meta.env.DEV,
        PROD: import.meta.env.PROD,
        MODE: import.meta.env.MODE,
        VITE_ENVIRONMENT: import.meta.env.VITE_ENVIRONMENT,
        VITE_DOMAIN: import.meta.env.VITE_DOMAIN,
        VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
        VITE_SECURE: import.meta.env.VITE_SECURE,
        VITE_CLIENT1_PORT: import.meta.env.VITE_CLIENT1_PORT,
        VITE_CLIENT2_PORT: import.meta.env.VITE_CLIENT2_PORT,
        VITE_CLIENT3_PORT: import.meta.env.VITE_CLIENT3_PORT,
        VITE_JBSUSA_PORT: import.meta.env.VITE_JBSUSA_PORT,
        VITE_AUTH_PORT: import.meta.env.VITE_AUTH_PORT,
        VITE_API_PORT: import.meta.env.VITE_API_PORT
      },
      location: {
        hostname: window.location.hostname,
        port: window.location.port,
        protocol: window.location.protocol,
        href: window.location.href
      }
    };
  }
}

// Export singleton instance
export const configService = new ConfigService();

// Export convenience functions
export const getConfig = () => configService.getConfig();
export const getServiceUrl = (service: keyof EnvironmentConfig['services'], path?: string) => 
  configService.getServiceUrl(service, path);
export const isDevelopment = () => configService.isDevelopment();
export const isProduction = () => configService.isProduction();

// Default export
export default configService; 